import React, { useCallback } from 'react';
import { TrackingClient } from '@sparefoot/tracking-client';
import { omit } from 'lodash';

/**
 * Custom hook for form tracking.
 * @param {Object} props - Component props with onSubmit, id
 * @returns {{ handleSubmit: Function }}
 */
export const useFormTracking = ({ onSubmit, id }) => {
	const handleSubmit = useCallback(
		(evt) => {
			evt.stopPropagation();
			evt.preventDefault();

			const form = evt.target;
			const formData = new FormData(form);

			const trackingData = {
				form_id: form.id || id || form.className || '',
				form_target: form.action,
				form_url: form?.baseURI,
				email: formData.get('email') || undefined,
				first_name:
					formData.get('firstName') ||
					formData.get('alternateFirstNames') ||
					undefined,
				last_name:
					formData.get('lastName') ||
					formData.get('alternateLastNames') ||
					undefined,
				phone: formData.get('phone') || undefined,
			};

			TrackingClient.trackFormSubmitted(trackingData);

			return Promise.resolve(onSubmit(evt));
		},
		[onSubmit, id],
	);

	return { handleSubmit };
};

/**
 * HOC wrapper for form tracking (backward compatible).
 */
const withFormTracking = (WrappedComponent) => {
	const WithFormTracking = (props) => {
		const { handleSubmit } = useFormTracking({
			onSubmit: props.onSubmit,
			id: props.id,
		});
		const passProps = omit(props, [
			'handleSubmit',
			'invalid',
			'successLabel',
			'failureLabel',
		]);
		return (
			<WrappedComponent
				{...passProps}
				onSubmit={handleSubmit}
			/>
		);
	};
	WithFormTracking.displayName = `withFormTracking(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	return WithFormTracking;
};

export default withFormTracking;
